'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  X,
  Loader2,
  BarChart3,
  Play,
  TrendingDown,
  TrendingUp,
  Target,
  Info,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { useEquaFlowStore } from '@/lib/store';
import { solveInBrowser } from '@/lib/browser-solver';
import { detectFieldRole, isFormula } from '@/lib/formula';
import { formatNumber } from '@/lib/format';
import { experimentRequest, normalizeSolveResults, selectSolveResult } from '@/lib/scenario-experiment';
import type { Field, SolveRequest, SolveResponse } from '@/lib/types';

function currentSensitivityRevision() {
  const state = useEquaFlowStore.getState();
  return JSON.stringify([state.currentProjectId, state.activeScenario, state.scenarios, state.toSolveRequest(), Object.values(state.entities).map((entity) => [entity.id, entity.fields.map((field) => [field.id, field.value, field.scenarioValues])])]);
}

// ── Types ──
interface SensitivityInput {
  entityId: string;
  entityName: string;
  fieldId: string;
  fieldName: string;
  baseValue: number;
  formatUnit: string;
  formatDecimalPlaces: number | null;
}

interface SensitivityOutput {
  entityId: string;
  entityName: string;
  fieldId: string;
  fieldName: string;
  formatUnit: string;
  formatDecimalPlaces: number | null;
}

interface SensitivityResult {
  inputFieldId: string;
  inputLabel: string;
  lowValue: number;
  baseValue: number;
  highValue: number;
  lowInput: number;
  highInput: number;
  impact: number;
}

// ── Tornado Chart Colors ──
const LOW_COLOR = '#ef4444';   // red-500
const LOW_COLOR_LIGHT = '#fca5a5'; // red-300
const HIGH_COLOR = '#22c55e';  // green-500
const HIGH_COLOR_LIGHT = '#86efac'; // green-300

function SensitivityTooltip({
  active,
  payload,
  variationPct,
  decimalPlaces,
  unit,
}: {
  active?: boolean;
  payload?: Array<{ payload?: { name: string; lowValue: number; highValue: number; baseValue: number; impact: number } }>;
  variationPct: number;
  decimalPlaces: number;
  unit: import('@/lib/types').FieldFormatUnit;
}) {
  if (!active || !payload?.length) return null;
  const data = payload[0]?.payload;
  if (!data) return null;
  return (
    <div className="bg-popover border border-border rounded-lg shadow-lg px-3 py-2 text-xs space-y-1.5 min-w-[180px]">
      <div className="font-semibold text-foreground border-b border-border pb-1">{data.name}</div>
      <div className="flex items-center justify-between gap-3">
        <span className="flex items-center gap-1 text-red-600 dark:text-red-400">
          <TrendingDown className="h-3 w-3" /> Нижний вариант (−{variationPct}%):
        </span>
        <span className="font-mono font-medium">{formatNumber(data.lowValue, decimalPlaces, unit)}</span>
      </div>
      <div className="flex items-center justify-between gap-3">
        <span className="text-muted-foreground">База:</span>
        <span className="font-mono">{formatNumber(data.baseValue, decimalPlaces, unit)}</span>
      </div>
      <div className="flex items-center justify-between gap-3">
        <span className="flex items-center gap-1 text-green-600 dark:text-green-400">
          <TrendingUp className="h-3 w-3" /> Верхний вариант (+{variationPct}%):
        </span>
        <span className="font-mono font-medium">{formatNumber(data.highValue, decimalPlaces, unit)}</span>
      </div>
      <div className="border-t border-border pt-1 flex items-center justify-between gap-3">
        <span className="font-semibold">Размах результатов:</span>
        <span className="font-mono font-bold text-amber-600 dark:text-amber-400">
          {formatNumber(data.impact, decimalPlaces, unit)}
        </span>
      </div>
    </div>
  );
}

// ── Recharts Tornado Chart ──
function TornadoChart({
  results,
  targetField,
  decimalPlaces,
  variationPct,
}: {
  results: SensitivityResult[];
  targetField: SensitivityOutput | undefined;
  decimalPlaces: number;
  variationPct: number;
}) {
  if (results.length === 0) return null;

  const dp = targetField?.formatDecimalPlaces ?? decimalPlaces;
  const unit = (targetField?.formatUnit ?? 'none') as import('@/lib/types').FieldFormatUnit;

  // Build chart data: deviation from base value
  // low = negative deviation (lowValue - baseValue), high = positive deviation (highValue - baseValue)
  const chartData = results.map((r) => ({
    name: r.inputLabel,
    low: r.lowValue - r.baseValue,  // negative number
    high: r.highValue - r.baseValue, // positive number
    lowValue: r.lowValue,
    highValue: r.highValue,
    baseValue: r.baseValue,
    impact: r.impact,
    lowInput: r.lowInput,
    highInput: r.highInput,
  }));

  // Compute a nice symmetric domain for X axis
  const maxDev = Math.max(
    ...results.map((r) => Math.max(Math.abs(r.lowValue - r.baseValue), Math.abs(r.highValue - r.baseValue))),
    0.001,
  );
  const domainMax = maxDev * 1.15; // 15% padding
  const domainMin = -domainMax;

  return (
    <div className="w-full space-y-2">
      {/* Chart header */}
      <div className="flex items-center justify-between text-[10px] px-1">
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1">
            <span className="inline-block w-3 h-2.5 rounded-sm" style={{ backgroundColor: LOW_COLOR }} />
            <span className="text-red-600 dark:text-red-400 font-medium">−{variationPct}%</span>
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block w-3 h-2.5 rounded-sm" style={{ backgroundColor: HIGH_COLOR }} />
            <span className="text-green-600 dark:text-green-400 font-medium">+{variationPct}%</span>
          </span>
        </div>
        <span className="text-muted-foreground font-mono">
          База: {formatNumber(results[0].baseValue, dp, unit)}
        </span>
      </div>

      {/* Recharts Bar Chart */}
      <ResponsiveContainer width="100%" height={Math.max(120, results.length * 38 + 30)}>
        <BarChart
          data={chartData}
          layout="vertical"
          margin={{ top: 5, right: 30, left: 10, bottom: 5 }}
          barCategoryGap="25%"
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="currentColor"
            strokeOpacity={0.1}
            horizontal={false}
          />
          <XAxis
            type="number"
            domain={[domainMin, domainMax]}
            tickFormatter={(v: number) => {
              if (Math.abs(v) < 0.001) return '0';
              const sign = v < 0 ? '-' : '+';
              return `${sign}${formatNumber(Math.abs(v), dp, unit)}`;
            }}
            tick={{ fontSize: 9, fontFamily: 'monospace' }}
            stroke="currentColor"
            strokeOpacity={0.3}
          />
          <YAxis
            type="category"
            dataKey="name"
            width={120}
            tick={{ fontSize: 10, fontFamily: 'monospace' }}
            stroke="currentColor"
            strokeOpacity={0.3}
          />
          <Tooltip content={<SensitivityTooltip variationPct={variationPct} decimalPlaces={dp} unit={unit} />} />
          <ReferenceLine x={0} stroke="currentColor" strokeOpacity={0.4} strokeWidth={1.5} />

          {/* Low bars (negative deviation) */}
          <Bar
            dataKey="low"
            name="Decrease"
            radius={[0, 0, 0, 0]}
            maxBarSize={24}
          >
            {chartData.map((entry, index) => (
              <Cell
                key={`low-${index}`}
                fill={LOW_COLOR}
                fillOpacity={0.7 - index * 0.08}
              />
            ))}
          </Bar>

          {/* High bars (positive deviation) */}
          <Bar
            dataKey="high"
            name="Increase"
            radius={[0, 0, 0, 0]}
            maxBarSize={24}
          >
            {chartData.map((entry, index) => (
              <Cell
                key={`high-${index}`}
                fill={HIGH_COLOR}
                fillOpacity={0.7 - index * 0.08}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      {/* Show the measured range without implying an absolute risk category. */}
      <div className="flex flex-wrap gap-1.5 pt-1">
        {results.map((r) => {
          return (
            <span
              key={r.inputFieldId}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-medium bg-muted text-muted-foreground"
            >
              {r.inputLabel}
              <span className="font-mono">Размах {formatNumber(r.impact, dp, unit)}</span>
            </span>
          );
        })}
      </div>
    </div>
  );
}

// ── Main Component ──

export function SensitivityPanel({ onClose }: { onClose: () => void }) {
  const entities = useEquaFlowStore((s) => s.entities);
  const activeScenario = useEquaFlowStore((s) => s.activeScenario);
  const currentProjectId = useEquaFlowStore((s) => s.currentProjectId);
  const scenarios = useEquaFlowStore((s) => s.scenarios);
  const optimizationTargets = useEquaFlowStore((s) => s.optimizationTargets);
  const timeEnabled = useEquaFlowStore((s) => s.timeEnabled);
  const timeHorizon = useEquaFlowStore((s) => s.timeHorizon);
  const timestepLabels = useEquaFlowStore((s) => s.timestepLabels);
  const decimalPlaces = useEquaFlowStore((s) => s.decimalPlaces);

  // Config
  const [targetFieldId, setTargetFieldId] = useState<string>('');
  const [variationPct, setVariationPct] = useState(10);
  const [solving, setSolving] = useState(false);
  const [solveProgress, setSolveProgress] = useState(0);
  const [results, setResults] = useState<SensitivityResult[]>([]);
  const [error, setError] = useState<string | null>(null);
  const analysisGeneration = useRef(0);

  const revision = useMemo(() => currentSensitivityRevision(), [currentProjectId, activeScenario, scenarios, entities, timeEnabled, timeHorizon, timestepLabels, optimizationTargets]);
  useEffect(() => {
    analysisGeneration.current++;
    setResults([]);
    setError(null);
    setSolving(false);
    setSolveProgress(0);
  }, [revision, targetFieldId, variationPct]);

  // Collect input fields (numeric inputs)
  const inputFields = useMemo(() => {
    const result: SensitivityInput[] = [];
    for (const entity of Object.values(entities)) {
      for (const field of entity.fields) {
        const role = detectFieldRole(field.status, field.value);
        const activeValue = field.scenarioValues?.[activeScenario] ?? field.value;
        if (role === 'input' && activeValue !== null && !isFormula(String(activeValue))) {
          const numVal = typeof activeValue === 'number' ? activeValue : Number(activeValue);
          if (!isNaN(numVal)) {
            result.push({
              entityId: entity.id,
              entityName: entity.displayName ?? entity.entityType,
              fieldId: field.id,
              fieldName: field.label ?? field.name,
              baseValue: numVal,
              formatUnit: field.formatUnit ?? 'none',
              formatDecimalPlaces: field.formatDecimalPlaces ?? null,
            });
          }
        }
      }
    }
    return result;
  }, [entities, activeScenario]);

  // Collect output/formula fields (candidates for target)
  const outputFields = useMemo(() => {
    const result: SensitivityOutput[] = [];
    for (const entity of Object.values(entities)) {
      for (const field of entity.fields) {
        const role = detectFieldRole(field.status, field.value);
        if (role === 'output' || role === 'formula') {
          result.push({
            entityId: entity.id,
            entityName: entity.displayName ?? entity.entityType,
            fieldId: field.id,
            fieldName: field.label ?? field.name,
            formatUnit: field.formatUnit ?? 'none',
            formatDecimalPlaces: field.formatDecimalPlaces ?? null,
          });
        }
      }
    }
    return result;
  }, [entities]);

  // Auto-select first output field if none selected
  const effectiveTarget = targetFieldId || outputFields[0]?.fieldId || '';

  // Run bounded sequential sensitivity solves; only real successful solver values are plotted.
  const handleAnalyze = useCallback(async () => {
    if (!effectiveTarget || inputFields.length === 0) return;
    const expectedRevision = currentSensitivityRevision();
    const generation = ++analysisGeneration.current;
    const isCurrent = () => generation === analysisGeneration.current && currentSensitivityRevision() === expectedRevision;
    setSolving(true);
    setError(null);
    setResults([]);
    setSolveProgress(0);

    try {
      // Step 1: Solve base case
      const store = useEquaFlowStore.getState();
      const baseRequest = store.toSolveRequest();
      const baseData: SolveResponse = await solveInBrowser(baseRequest);
      if (!isCurrent()) return;
      if (baseData.status !== 'SOLVED' && baseData.status !== 'OPTIMIZED' && baseData.status !== 'PARTIAL') {
        throw new Error('Base model has no solution — fix conflicts first');
      }

      // Extract base value for target field
      const baseResults = normalizeSolveResults(baseData, baseRequest);
      const baseValueCandidate = selectSolveResult(baseResults, effectiveTarget, Math.max(0, timeHorizon - 1));
      if (typeof baseValueCandidate !== 'number' || !Number.isFinite(baseValueCandidate)) {
        throw new Error('Target field has no numeric solution');
      }
      const baseValue = baseValueCandidate;
      setSolveProgress(10);

      // Step 2: Build real solver requests for each low/high input variation.
      const factor = variationPct / 100;
      const variations: { input: SensitivityInput; direction: 'low' | 'high'; modifiedValue: number }[] = [];

      for (const input of inputFields) {
        variations.push({
          input,
          direction: 'low',
          modifiedValue: input.baseValue * (1 - factor),
        });
        variations.push({
          input,
          direction: 'high',
          modifiedValue: input.baseValue * (1 + factor),
        });
      }

      const solveResults: Map<string, { lowValue: number; highValue: number }> = new Map();

      for (let i = 0; i < variations.length; i++) {
        const variation = variations[i];
        const request: SolveRequest = experimentRequest(baseRequest, { [variation.input.fieldId]: variation.modifiedValue });
        const data: SolveResponse = await solveInBrowser(request);
        if (!isCurrent()) return;
        if (!['SOLVED', 'OPTIMIZED', 'PARTIAL'].includes(data.status)) {
          throw new Error(`Не удалось рассчитать изменение «${variation.input.fieldName}». Результаты анализа не показаны.`);
        }
        const normalized = normalizeSolveResults(data, request);
        const resultValue = selectSolveResult(normalized, effectiveTarget, Math.max(0, timeHorizon - 1));
        if (typeof resultValue !== 'number' || !Number.isFinite(resultValue)) {
          throw new Error(`Решатель не вернул числовое значение показателя «${outputFields.find((field) => field.fieldId === effectiveTarget)?.fieldName ?? effectiveTarget}».`);
        }
        if (!solveResults.has(variation.input.fieldId)) solveResults.set(variation.input.fieldId, { lowValue: baseValue, highValue: baseValue });
        const entry = solveResults.get(variation.input.fieldId)!;
        if (variation.direction === 'low') entry.lowValue = resultValue;
        else entry.highValue = resultValue;
        const progress = 10 + Math.round(((i + 1) / variations.length) * 90);
        setSolveProgress(progress);
      }

      // Build final results
      const newResults: SensitivityResult[] = inputFields.map((input) => {
        const entry = solveResults.get(input.fieldId) ?? { lowValue: baseValue, highValue: baseValue };
        return {
          inputFieldId: input.fieldId,
          inputLabel: `${input.entityName}.${input.fieldName}`,
          lowValue: entry.lowValue,
          baseValue,
          highValue: entry.highValue,
          lowInput: input.baseValue * (1 - factor),
          highInput: input.baseValue * (1 + factor),
          impact: Math.abs(entry.highValue - entry.lowValue),
        };
      });

      // Sort by impact (descending) — tornado chart style
      newResults.sort((a, b) => b.impact - a.impact);
      setResults(newResults);
      setSolveProgress(100);
    } catch (err) {
      if (isCurrent()) {
        setError(String(err));
        setResults([]);
      }
    } finally {
      if (isCurrent()) {
        setSolving(false);
        setSolveProgress(0);
      }
    }
  }, [effectiveTarget, inputFields, variationPct]);

  const maxImpact = results.length > 0 ? Math.max(...results.map((r) => r.impact)) : 0;
  const hasResults = results.length > 0;

  // Find target field metadata
  const targetField = outputFields.find((f) => f.fieldId === effectiveTarget);
  const dp = targetField?.formatDecimalPlaces ?? decimalPlaces;
  const unit = (targetField?.formatUnit ?? 'none') as import('@/lib/types').FieldFormatUnit;

  return (
    <div className="h-full flex flex-col bg-card">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b shrink-0">
        <h3 className="font-semibold text-sm flex items-center gap-1.5">
          <BarChart3 className="h-4 w-4 text-amber-500" />
          Анализ чувствительности
        </h3>
        <div className="flex items-center gap-1">
          {solving && (
            <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" />
              <span>{solveProgress}%</span>
            </span>
          )}
          <Button variant="ghost" size="icon" className="h-7 w-7" aria-label="Закрыть анализ чувствительности" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Config section */}
      <div className="px-4 py-3 border-b shrink-0 space-y-3">
        {/* Target field selector */}
        <div className="space-y-1">
          <label className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium flex items-center gap-1">
            <Target className="h-3 w-3" /> Показатель для сравнения
          </label>
          <Select value={effectiveTarget} onValueChange={setTargetFieldId}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue placeholder="Выберите показатель…" />
            </SelectTrigger>
            <SelectContent>
              {outputFields.map((f) => (
                <SelectItem key={f.fieldId} value={f.fieldId}>
                  {f.entityName}.{f.fieldName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Variation % slider */}
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <label className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">
              Изменение входных значений
            </label>
            <span className="text-xs font-mono text-amber-600 dark:text-amber-400 font-semibold">±{variationPct}%</span>
          </div>
          <Slider
            aria-label="Изменение входных значений в процентах"
            value={[variationPct]}
            min={1}
            max={50}
            step={1}
            onValueChange={([v]) => setVariationPct(v)}
            className="w-full"
          />
          <div className="flex justify-between text-[9px] text-muted-foreground font-mono">
            <span>1%</span>
            <span>25%</span>
            <span>50%</span>
          </div>
        </div>
        <p className="text-[10px] text-muted-foreground">Это заданная проверка «что если», не вероятность и не прогноз.</p>

        {/* Progress bar */}
        {solving && (
          <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-amber-500 to-orange-500 transition-all duration-300 ease-out rounded-full"
              style={{ width: `${solveProgress}%` }}
            />
          </div>
        )}

        {/* Analyze button */}
        <Button
          onClick={handleAnalyze}
          disabled={!effectiveTarget || inputFields.length === 0 || solving}
          className="w-full h-9 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white"
        >
          {solving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Считаем ({inputFields.length} входных значений)…
            </>
          ) : (
            <>
              <Play className="h-4 w-4 mr-2" />
              Рассчитать чувствительность
            </>
          )}
        </Button>
      </div>

      {/* Error */}
      {error && (
        <div className="px-4 py-2 bg-red-50 dark:bg-red-950/30 border-b border-red-200 dark:border-red-800 shrink-0">
          <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Tornado chart results */}
      {hasResults && (
        <ScrollArea className="flex-1">
          <div className="p-4 space-y-4">
            {/* Summary cards */}
            <div className="flex items-stretch gap-3 text-xs">
              <div className="flex-1 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 px-3 py-2 space-y-1">
                <div className="text-[9px] text-amber-600 dark:text-amber-400 uppercase tracking-wider font-medium flex items-center gap-1">
                  <Target className="h-3 w-3" /> Показатель
                </div>
                <div className="font-mono font-semibold text-amber-900 dark:text-amber-200 text-sm">
                  {targetField?.entityName}.{targetField?.fieldName}
                </div>
                <div className="font-mono text-amber-700 dark:text-amber-300">
                  = {formatNumber(results[0].baseValue, dp, unit)}
                </div>
              </div>
              <div className="flex-1 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 px-3 py-2 space-y-1">
                <div className="text-[9px] text-blue-600 dark:text-blue-400 uppercase tracking-wider font-medium">
                  Проверено входных значений
                </div>
                <div className="font-mono font-semibold text-blue-900 dark:text-blue-200 text-sm">
                  {results.length}
                </div>
                <div className="font-mono text-blue-700 dark:text-blue-300">
                  ±{variationPct}% для каждого входа
                </div>
              </div>
              <div className="flex-1 rounded-lg bg-purple-50 dark:bg-purple-950/30 border border-purple-200 dark:border-purple-800 px-3 py-2 space-y-1">
                <div className="text-[9px] text-purple-600 dark:text-purple-400 uppercase tracking-wider font-medium">
                  Наибольший размах
                </div>
                <div className="font-mono font-semibold text-purple-900 dark:text-purple-200 text-sm">
                  {formatNumber(maxImpact, dp, unit)}
                </div>
                <div className="font-mono text-purple-700 dark:text-purple-300">
                  {results[0].inputLabel}
                </div>
              </div>
            </div>

            {/* Recharts Tornado Chart */}
            <div className="rounded-lg border bg-background p-3">
              <TornadoChart
                results={results}
                targetField={targetField}
                decimalPlaces={decimalPlaces}
                variationPct={variationPct}
              />
            </div>

            {/* How to read this chart */}
            <div className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50/40 dark:bg-blue-950/20 px-3 py-2 flex items-start gap-2">
              <Info className="h-3.5 w-3.5 text-blue-500 mt-0.5 shrink-0" />
              <p className="text-[11px] text-blue-800 dark:text-blue-200 leading-relaxed">
                <span className="font-semibold">Как читать график:</span> Каждая строка показывает, как изменяется показатель при изменении одного входа на ±{variationPct}%.
                <span className="text-red-500 font-medium"> Красные полосы</span> показывают снижение,
                <span className="text-green-500 font-medium"> зелёные полосы</span> — рост.
                Чем длиннее полоса, тем больше размах. Сверху расположены входы с наибольшим размахом результата.
              </p>
            </div>

            {/* Input detail table */}
            <div className="space-y-1.5">
            <div className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">
                Результаты расчёта
              </div>
              <div className="rounded-lg border overflow-hidden">
                <table className="w-full text-[10px]">
                  <thead>
                    <tr className="bg-muted/50">
                      <th className="text-left px-2 py-1.5 font-medium">Вход</th>
                      <th className="text-right px-2 py-1.5 font-medium text-red-500">Нижний</th>
                      <th className="text-right px-2 py-1.5 font-medium">База</th>
                      <th className="text-right px-2 py-1.5 font-medium text-emerald-500">Верхний</th>
                      <th className="text-right px-2 py-1.5 font-medium">Размах</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((r) => {
                      const odp = targetField?.formatDecimalPlaces ?? decimalPlaces;
                      const ounit = (targetField?.formatUnit ?? 'none') as import('@/lib/types').FieldFormatUnit;
                      const impactColor = 'text-foreground';

                      // Percentage change from base
                      const pctChange = r.baseValue !== 0 ? ((r.highValue - r.lowValue) / Math.abs(r.baseValue)) * 100 : null;

                      return (
                        <tr key={r.inputFieldId} className="border-t border-border/50 hover:bg-muted/30">
                          <td className="px-2 py-1.5">
                            <div className="font-mono font-medium">{r.inputLabel}</div>
                          </td>
                          <td className="px-2 py-1.5 font-mono text-right text-red-500">
                            {formatNumber(r.lowValue, dp, unit)}
                          </td>
                          <td className="px-2 py-1.5 font-mono text-right">
                            {formatNumber(r.baseValue, dp, unit)}
                          </td>
                          <td className="px-2 py-1.5 font-mono text-right text-emerald-500">
                            {formatNumber(r.highValue, dp, unit)}
                          </td>
                          <td className="px-2 py-1.5 text-right">
                            <div className={`font-mono font-semibold ${impactColor}`}>
                              {formatNumber(r.impact, odp, ounit)}
                            </div>
                            <div className="text-[9px] text-muted-foreground font-mono">
                              {pctChange === null ? '—' : `${pctChange.toFixed(1)}%`}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Key insight */}
            {results.length > 0 && (
              <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-950/20 px-3 py-2.5 space-y-1">
                <div className="text-[10px] font-medium text-amber-700 dark:text-amber-400 uppercase tracking-wider">
                  Итог проверки
                </div>
                <p className="text-xs text-amber-900 dark:text-amber-200 leading-relaxed">
                  <span className="font-semibold">{results[0].inputLabel}</span> дала наибольшую разницу для показателя{' '}
                  <span className="font-semibold">{targetField?.entityName}.{targetField?.fieldName}</span>
                  {' '}при изменении входного значения на ±{variationPct}%: разница результатов{' '}
                  <span className="font-semibold">
                    {formatNumber(results[0].impact, dp, unit)}
                  </span>
                  {results[0].baseValue !== 0 && (
                    <span className="text-muted-foreground"> ({((results[0].impact / Math.abs(results[0].baseValue)) * 100).toFixed(1)}% swing)</span>
                  )}.
                  {results.length > 1 && (
                    <> Наименьшая разница получилась для{' '}
                      <span className="font-semibold">{results[results.length - 1].inputLabel}</span>{' '}
                      (размах: {formatNumber(results[results.length - 1].impact, dp, unit)}).
                    </>
                  )}
                </p>
              </div>
            )}
          </div>
        </ScrollArea>
      )}

      {/* Empty state */}
      {!hasResults && !solving && (
        <div className="flex-1 flex items-center justify-center p-6 text-center">
          <div className="space-y-3">
            <div className="text-3xl opacity-20">🌪️</div>
            <p className="text-sm text-muted-foreground">
              Анализ чувствительности
            </p>
            <p className="text-xs text-muted-foreground max-w-[240px] mx-auto">
              Меняет каждый вход на выбранный процент и пересчитывает выбранный показатель.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
